import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Store, 
  Package, 
  Plus, 
  Users,
  Clock,
  CheckCircle,
  Truck,
  IndianRupee,
  Calendar,
  Phone,
  MapPin,
  Eye,
  ChevronDown,
  ShoppingBag,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LocationSelector } from '../components/LocationSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShopDashboard = () => {
  const { user, getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const [shops, setShops] = useState([]);
  const [selectedShop, setSelectedShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [showCreateShop, setShowCreateShop] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'orders', 'products'
  const [orderFilter, setOrderFilter] = useState('all'); // 'all', 'pending', 'assigned', 'delivered'

  const [shopForm, setShopForm] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    location: null
  });

  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    stock: '',
    image_url: ''
  });

  useEffect(() => {
    fetchShops();
    fetchDeliveryAgents();
  }, []);

  useEffect(() => {
    if (shops.length > 0) {
      fetchAllOrders();
      if (selectedShop) {
        fetchProducts();
      }
    }
  }, [shops, selectedShop]);

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops/owner/my-shops`, { headers: getAuthHeader() });
      setShops(response.data);
      if (response.data.length > 0 && !selectedShop) {
        setSelectedShop(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load shops:', error);
    }
  };

  const fetchProducts = async () => {
    if (!selectedShop) return;
    try {
      const response = await axios.get(`${API}/products?shop_id=${selectedShop.id}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  const fetchAllOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { headers: getAuthHeader() });
      // Filter orders for all shops owned by this user
      const shopIds = shops.map(s => s.id);
      const myOrders = response.data.filter(o => shopIds.includes(o.shop_id));
      setAllOrders(myOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Failed to load orders:', error);
    }
  };

  const fetchDeliveryAgents = async () => {
    try {
      const response = await axios.get(`${API}/delivery-agents`, { headers: getAuthHeader() });
      setDeliveryAgents(response.data);
    } catch (error) {
      console.error('Failed to load delivery agents:', error);
    }
  };

  const createShop = async (e) => {
    e.preventDefault();
    const finalLocation = shopForm.location || { address: shopForm.address, lat: 0, lng: 0 };

    setLoading(true);
    try {
      await axios.post(`${API}/shops`, { ...shopForm, location: finalLocation }, { headers: getAuthHeader() });
      toast.success(t('shopCreated'));
      setShowCreateShop(false);
      setShopForm({ name: '', description: '', address: '', phone: '', location: null });
      fetchShops();
    } catch (error) {
      toast.error(t('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${API}/products?shop_id=${selectedShop.id}`,
        {
          ...productForm,
          price: parseFloat(productForm.price),
          stock: parseInt(productForm.stock)
        },
        { headers: getAuthHeader() }
      );
      toast.success(t('productAdded'));
      setShowAddProduct(false);
      setProductForm({ name: '', description: '', price: '', category: '', stock: '', image_url: '' });
      fetchProducts();
    } catch (error) {
      toast.error(t('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const assignAgent = async (orderId, agentId) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/assign?agent_id=${agentId}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(t('agentAssigned'));
      fetchAllOrders();
    } catch (error) {
      toast.error(t('loadFailed'));
    }
  };

  const getStats = () => {
    const totalOrders = allOrders.length;
    const pendingOrders = allOrders.filter(o => o.status === 'pending').length;
    const deliveredOrders = allOrders.filter(o => o.status === 'delivered').length;
    const totalRevenue = allOrders.filter(o => o.status === 'delivered').reduce((sum, o) => sum + o.total_amount, 0);
    const todayOrders = allOrders.filter(o => {
      const orderDate = new Date(o.created_at).toDateString();
      const today = new Date().toDateString();
      return orderDate === today;
    }).length;
    return { totalOrders, pendingOrders, deliveredOrders, totalRevenue, todayOrders };
  };

  const stats = getStats();

  const filteredOrders = orderFilter === 'all' 
    ? allOrders 
    : allOrders.filter(o => o.status === orderFilter);

  const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: t('pending') },
    assigned: { color: 'bg-blue-100 text-blue-800', icon: Truck, label: t('assigned') },
    picked_up: { color: 'bg-purple-100 text-purple-800', icon: Package, label: t('pickedUp') },
    delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: t('delivered') }
  };

  const availableAgents = deliveryAgents.filter(a => a.is_available !== false);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-bold text-3xl md:text-4xl text-gray-900 mb-2">
              {t('shopOwnerDashboard')}
            </h1>
            <p className="text-gray-600">{t('manageShopsProducts')}</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button
              data-testid="add-shop-btn"
              onClick={() => setShowCreateShop(true)}
              className="bg-emerald-500 text-white hover:bg-emerald-600 h-12 px-6 rounded-full shadow-lg"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('addShop')}
            </Button>
            {selectedShop && (
              <Button
                data-testid="add-product-btn"
                onClick={() => setShowAddProduct(true)}
                className="bg-orange-500 text-white hover:bg-orange-600 h-12 px-6 rounded-full shadow-lg"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('addProduct')}
              </Button>
            )}
          </div>
        </div>

        {/* Shop Selector */}
        {shops.length > 1 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm mb-6">
            <Label className="text-gray-700 mb-2 block">{t('selectShop')}</Label>
            <Select value={selectedShop?.id} onValueChange={(id) => setSelectedShop(shops.find(s => s.id === id))}>
              <SelectTrigger className="w-full md:w-64">
                <SelectValue placeholder="Select a shop" />
              </SelectTrigger>
              <SelectContent>
                {shops.map((shop) => (
                  <SelectItem key={shop.id} value={shop.id}>
                    {shop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Store className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-sm text-gray-600">{t('totalShops')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{shops.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-orange-600" />
              </div>
              <span className="text-sm text-gray-600">{t('totalProducts')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{products.length}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-gray-600">{t('totalOrders')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{stats.totalOrders}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <span className="text-sm text-gray-600">{t('pendingOrders')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">{stats.pendingOrders}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-gray-600">{t('revenue')}</span>
            </div>
            <p className="font-bold text-2xl text-gray-900">₹{stats.totalRevenue}</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 rounded-full font-semibold whitespace-nowrap transition-all ${
              activeTab === 'overview' 
                ? 'bg-emerald-500 text-white shadow-lg' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <TrendingUp className="w-5 h-5 inline mr-2" />
            {t('overview')}
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 rounded-full font-semibold whitespace-nowrap transition-all ${
              activeTab === 'orders' 
                ? 'bg-emerald-500 text-white shadow-lg' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <ShoppingBag className="w-5 h-5 inline mr-2" />
            {t('orders')} ({allOrders.length})
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-3 rounded-full font-semibold whitespace-nowrap transition-all ${
              activeTab === 'products' 
                ? 'bg-emerald-500 text-white shadow-lg' 
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            <Package className="w-5 h-5 inline mr-2" />
            {t('products')} ({products.length})
          </button>
        </div>

        {shops.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-bold text-xl text-gray-900 mb-2">{t('noShopsYet')}</h3>
            <p className="text-gray-600 mb-6">{t('createFirstShop')}</p>
            <Button
              data-testid="create-first-shop-btn"
              onClick={() => setShowCreateShop(true)}
              className="bg-emerald-500 text-white hover:bg-emerald-600 h-12 px-8 rounded-full shadow-lg"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('createShop')}
            </Button>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && selectedShop && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-orange-100 rounded-2xl flex items-center justify-center">
                      <Store className="w-8 h-8 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-bold text-2xl text-gray-900">{selectedShop.name}</h2>
                      <p className="text-gray-600 mt-1">{selectedShop.description}</p>
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {selectedShop.address}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone size={14} />
                          {selectedShop.phone}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Orders */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-xl text-gray-900">{t('recentOrders')}</h3>
                    <Button variant="ghost" onClick={() => setActiveTab('orders')} className="text-emerald-600">
                      {t('viewAll')}
                    </Button>
                  </div>
                  {allOrders.slice(0, 5).map((order) => {
                    const status = statusConfig[order.status] || statusConfig.pending;
                    return (
                      <div key={order.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                        <div>
                          <p className="font-semibold text-gray-900">#{order.id.slice(0, 8)}</p>
                          <p className="text-sm text-gray-500">{order.items.length} {t('items')} • ₹{order.total_amount}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-4">
                {/* Order Filter */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {['all', 'pending', 'assigned', 'picked_up', 'delivered'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setOrderFilter(filter)}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                        orderFilter === filter
                          ? 'bg-gray-900 text-white'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {filter === 'all' ? t('allOrders') : statusConfig[filter]?.label || filter}
                      <span className="ml-1 text-xs opacity-70">
                        ({filter === 'all' ? allOrders.length : allOrders.filter(o => o.status === filter).length})
                      </span>
                    </button>
                  ))}
                </div>

                {filteredOrders.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                    <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{t('noOrdersYet')}</p>
                  </div>
                ) : (
                  filteredOrders.map((order) => {
                    const status = statusConfig[order.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    const shop = shops.find(s => s.id === order.shop_id);
                    return (
                      <div 
                        key={order.id} 
                        className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all"
                      >
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                                <StatusIcon size={14} />
                                {status.label}
                              </span>
                              <span className="text-sm text-gray-500">#{order.id.slice(0, 8)}</span>
                              {shop && <span className="text-sm text-emerald-600 font-medium">{shop.name}</span>}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Calendar size={14} />
                                {new Date(order.created_at).toLocaleString('en-IN')}
                              </span>
                              <span className="flex items-center gap-1">
                                <Package size={14} />
                                {order.items.map(i => `${i.product_name} x${i.quantity}`).join(', ')}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin size={14} />
                                {order.delivery_address}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-bold text-2xl text-gray-900">₹{order.total_amount}</p>
                            </div>
                            {order.status === 'pending' && (
                              <Select onValueChange={(agentId) => assignAgent(order.id, agentId)}>
                                <SelectTrigger className="w-48">
                                  <SelectValue placeholder={t('selectAgent')} />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableAgents.length === 0 ? (
                                    <SelectItem value="none" disabled>
                                      {t('noAgentsAvailable')}
                                    </SelectItem>
                                  ) : (
                                    availableAgents.map((agent) => (
                                      <SelectItem key={agent.id} value={agent.id}>
                                        {agent.name} - {agent.phone}
                                      </SelectItem>
                                    ))
                                  )}
                                </SelectContent>
                              </Select>
                            )}
                            <Button
                              variant="outline"
                              className="rounded-full"
                              onClick={() => setSelectedOrder(order)}
                            >
                              <Eye size={16} className="mr-1" />
                              {t('details')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Products Tab */}
            {activeTab === 'products' && (
              <div>
                {products.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="font-bold text-xl text-gray-900 mb-2">{t('noProductsYet')}</h3>
                    <p className="text-gray-600 mb-6">{t('addFirstProduct')}</p>
                    <Button
                      onClick={() => setShowAddProduct(true)}
                      className="bg-orange-500 text-white hover:bg-orange-600 h-12 px-8 rounded-full shadow-lg"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      {t('addProduct')}
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {products.map((product) => (
                      <div
                        key={product.id}
                        className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-all"
                      >
                        <div className="aspect-square bg-gray-100 rounded-xl mb-3 flex items-center justify-center">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover rounded-xl" />
                          ) : (
                            <Package className="w-12 h-12 text-gray-300" />
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900">{product.name}</h4>
                        <p className="text-sm text-gray-500 line-clamp-2">{product.description}</p>
                        <div className="flex items-center justify-between mt-3">
                          <p className="font-bold text-lg text-emerald-600">₹{product.price}</p>
                          <span className={`text-xs px-2 py-1 rounded-full ${product.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {t('stock')}: {product.stock}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Create Shop Dialog */}
        <Dialog open={showCreateShop} onOpenChange={setShowCreateShop}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{t('createShop')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={createShop} className="space-y-4">
              <div>
                <Label htmlFor="shop-name" className="text-gray-700">{t('shopName')}</Label>
                <Input
                  id="shop-name"
                  required
                  value={shopForm.name}
                  onChange={(e) => setShopForm({ ...shopForm, name: e.target.value })}
                  placeholder={t('shopName')}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="shop-description" className="text-gray-700">{t('shopDescription')}</Label>
                <Textarea
                  id="shop-description"
                  required
                  value={shopForm.description}
                  onChange={(e) => setShopForm({ ...shopForm, description: e.target.value })}
                  placeholder={t('describeShop')}
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="shop-phone" className="text-gray-700">{t('phone')}</Label>
                <Input
                  id="shop-phone"
                  type="tel"
                  required
                  value={shopForm.phone}
                  onChange={(e) => setShopForm({ ...shopForm, phone: e.target.value })}
                  placeholder="+91 9876543210"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="shop-address" className="text-gray-700">{t('address')}</Label>
                <Input
                  id="shop-address"
                  required
                  value={shopForm.address}
                  onChange={(e) => setShopForm({ ...shopForm, address: e.target.value })}
                  placeholder={t('fullAddress')}
                  className="mt-2"
                />
              </div>
              <LocationSelector
                onLocationSelect={(location) => setShopForm({ ...shopForm, location })}
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 text-white hover:bg-emerald-600 h-12 rounded-full shadow-lg font-semibold"
              >
                {loading ? t('loading') : t('createShop')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Product Dialog */}
        <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{t('addProduct')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={addProduct} className="space-y-4">
              <div>
                <Label className="text-gray-700">{t('productName')}</Label>
                <Input
                  required
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  placeholder={t('productName')}
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-gray-700">{t('productDescription')}</Label>
                <Textarea
                  required
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  placeholder={t('productDescription')}
                  className="mt-2"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">{t('price')} (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    required
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    placeholder="99"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label className="text-gray-700">{t('stock')}</Label>
                  <Input
                    type="number"
                    required
                    value={productForm.stock}
                    onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                    placeholder="100"
                    className="mt-2"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-700">{t('category')}</Label>
                <Input
                  required
                  value={productForm.category}
                  onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  placeholder={t('productCategory')}
                  className="mt-2"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 text-white hover:bg-orange-600 h-12 rounded-full shadow-lg font-semibold"
              >
                {loading ? t('loading') : t('addProduct')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Order Details Dialog */}
        <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">{t('orderDetails')}</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('orderId')}:</span>
                  <span className="font-mono">#{selectedOrder.id.slice(0, 8)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">{t('status')}:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig[selectedOrder.status]?.color}`}>
                    {statusConfig[selectedOrder.status]?.label}
                  </span>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">{t('orderItems')}</h4>
                  {selectedOrder.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between py-2 border-b border-gray-100 last:border-0">
                      <span>{item.product_name} x {item.quantity}</span>
                      <span className="font-semibold">₹{item.price * item.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-bold text-lg">{t('total')}:</span>
                  <span className="font-bold text-2xl text-emerald-600">₹{selectedOrder.total_amount}</span>
                </div>
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-2">{t('deliveryAddress')}</h4>
                  <p className="text-gray-600 flex items-start gap-2">
                    <MapPin size={16} className="mt-1" />
                    {selectedOrder.delivery_address}
                  </p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ShopDashboard;
